import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, Clock, Eye, CheckCircle, XCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import ApplicationReviewPanel from '../admin-dashboard/components/ApplicationReviewPanel';
import Button from '../../components/ui/Button';
import { cn } from '../../utils/cn';



// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, icon: IconComponent, value, loading, colorClass, borderClass, iconBgClass, iconColorClass, textColorClass }) => (
  <div className={cn('rounded-xl border p-4 flex items-center gap-4', colorClass, borderClass)}>
    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0', iconBgClass)}>
      <IconComponent className={cn('w-5 h-5', iconColorClass)} />
    </div>
    <div>
      <p className={cn('text-2xl font-bold', textColorClass)}>
        {loading ? (
          <span className="inline-block w-8 h-6 bg-gray-200 rounded animate-pulse" />
        ) : (
          value ?? 0
        )}
      </p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  </div>
);

const AdminApplicationsDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [stats, setStats] = useState({ submitted: 0, under_review: 0, approved: 0, rejected: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  const isAdmin = userProfile?.role === 'admin';

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const statuses = ['submitted', 'under_review', 'approved', 'rejected'];
      const counts = {};
      await Promise.all(
        statuses?.map(async (s) => {
          const { count } = await supabase
            ?.from('professional_applications')
            ?.select('*', { count: 'exact', head: true })
            ?.eq('application_status', s);
          counts[s] = count || 0;
        })
      );
      setStats(counts);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('fetchStats error:', err?.message);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      const channel = supabase
        ?.channel('admin-apps-dashboard-stats')
        ?.on('postgres_changes', { event: '*', schema: 'public', table: 'professional_applications' }, fetchStats)
        ?.subscribe();
      return () => supabase?.removeChannel(channel);
    }
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-xl shadow-sm p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">Accès Administrateur Requis</h1>
          <p className="text-muted-foreground text-sm mb-6">Cette page est réservée aux administrateurs.</p>
          <Button onClick={() => navigate('/admin-dashboard')} variant="default" size="default">
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  const summaryCards = [
    {
      key: 'submitted',
      label: 'En attente',
      icon: Clock,
      colorClass: 'bg-blue-50',
      iconBgClass: 'bg-blue-100',
      iconColorClass: 'text-blue-600',
      textColorClass: 'text-blue-700',
      borderClass: 'border-blue-200',
    },
    {
      key: 'under_review',
      label: 'En examen',
      icon: Eye,
      colorClass: 'bg-amber-50',
      iconBgClass: 'bg-amber-100',
      iconColorClass: 'text-amber-600',
      textColorClass: 'text-amber-700',
      borderClass: 'border-amber-200',
    },
    {
      key: 'approved',
      label: 'Approuvées',
      icon: CheckCircle,
      colorClass: 'bg-green-50',
      iconBgClass: 'bg-green-100',
      iconColorClass: 'text-green-600',
      textColorClass: 'text-green-700',
      borderClass: 'border-green-200',
    },
    {
      key: 'rejected',
      label: 'Rejetées',
      icon: XCircle,
      colorClass: 'bg-red-50',
      iconBgClass: 'bg-red-100',
      iconColorClass: 'text-red-600',
      textColorClass: 'text-red-700',
      borderClass: 'border-red-200',
    },
  ];

  const pendingCount = stats?.submitted + stats?.under_review;

  return (
    <>
      <Helmet>
        <title>Candidatures Professionnelles — Administration</title>
        <meta name="description" content="Tableau de bord centralisé pour examiner, approuver et rejeter les candidatures de professionnels." />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Top bar */}
        <div className="bg-card border-b border-border sticky top-0 z-30">
          <div className="max-w-screen-xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin-dashboard')}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Admin Dashboard
              </button>
              <span className="text-border">/</span>
              <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                <ClipboardCheck className="w-4 h-4 text-primary" />
                Candidatures Professionnelles
              </span>
            </div>
            <div className="flex items-center gap-3">
              {pendingCount > 0 && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 rounded-full text-xs font-semibold">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block" />
                  {pendingCount} en attente de décision
                </span>
              )}
              <Button
                onClick={fetchStats}
                disabled={statsLoading}
                variant="outline"
                size="sm"
                className="gap-1.5"
              >
                <RefreshCw className={cn('w-3.5 h-3.5', statsLoading && 'animate-spin')} />
                Actualiser
              </Button>
              <span className="text-xs text-muted-foreground">
                Mis à jour : {lastRefreshed?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-6 py-8 space-y-8">
          {/* Page Header */}
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
                <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                  <ClipboardCheck className="w-5 h-5 text-primary-foreground" />
                </div>
                Examen des Candidatures
              </h1>
              <p className="text-muted-foreground text-sm mt-1.5 ml-11">
                Examinez, approuvez ou rejetez les candidatures en lot — les emails de décision sont envoyés automatiquement.
              </p>
            </div>
          </div>

          {/* Summary KPI Strip — shadcn/ui Card style */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {summaryCards?.map((card) => (
              <KpiCard
                key={card?.key}
                label={card?.label}
                icon={card?.icon}
                value={stats?.[card?.key]}
                loading={statsLoading}
                colorClass={card?.colorClass}
                borderClass={card?.borderClass}
                iconBgClass={card?.iconBgClass}
                iconColorClass={card?.iconColorClass}
                textColorClass={card?.textColorClass}
              />
            ))}
          </div>

          {/* Info banner when there are pending items */}
          {pendingCount > 0 && !statsLoading && (
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
              <div>
                <span className="font-semibold">{pendingCount} candidature{pendingCount > 1 ? 's' : ''}</span> nécessite{pendingCount > 1 ? 'nt' : ''} une décision.
                Utilisez la sélection en lot pour traiter plusieurs candidatures simultanément.
                Un email de décision sera automatiquement envoyé à chaque candidat.
              </div>
            </div>
          )}

          {/* Main Review Panel */}
          <ApplicationReviewPanel />
        </div>
      </div>
    </>
  );
};

export default AdminApplicationsDashboard;
